import { useCallback, useEffect, useState } from 'react'

export type UseTimerParams = {
  endTime: number // in milliseconds
  refreshRate?: number // in milliseconds
  autoStart?: boolean
  onTimerEnds?: () => void
}

export type UseTimerReturn = {
  start: () => void
  stop: () => void
  reset: () => void
  currentTime: number
  progress: number
  done: boolean
  isRunning: boolean
}

export function useTimer({
  endTime,
  refreshRate = 100,
  autoStart = false,
  onTimerEnds
}: UseTimerParams): UseTimerReturn {
  const [isRunning, setIsRunning] = useState(autoStart)
  const [currentTime, setCurrentTime] = useState(0)

  useEffect(() => {
    if (currentTime >= endTime) {
      setIsRunning(false)
      if (isRunning) {
        onTimerEnds?.()
      }
      return
    }

    if (!isRunning) return

    let delay = refreshRate
    if (currentTime + delay > endTime) {
      delay = endTime - currentTime
    }

    const timeout = setTimeout(() => {
      setCurrentTime((prev) => prev + delay)
    }, delay)

    return () => clearTimeout(timeout)
  }, [isRunning, currentTime, endTime, refreshRate, onTimerEnds])

  const progress = currentTime / endTime
  const done = progress >= 1

  const start = useCallback(() => {
    if (currentTime < endTime) {
      setIsRunning(true)
    }
  }, [currentTime, endTime])

  const stop = useCallback(() => {
    setIsRunning(false)
  }, [])

  const reset = useCallback(() => {
    setIsRunning(false)
    setCurrentTime(0)
  }, [])

  return { start, stop, reset, currentTime, progress, done, isRunning }
}
